/* GraphExplorer — Noam Graph : le réseau de votre organisation, navigable.
 * Entités ↔ risques ↔ mandats ↔ chaînes ↔ opportunités.
 * Double-clic sur une entité → déploie ses connexions cross-module.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactFlow, Background, Controls, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import { Loader2, RefreshCw, Search, Network, Maximize2 } from "lucide-react";

export const TYPE_STYLE = {
  entity:   { color: "#EFD127", bg: "#3a2f00", label: "Entité" },
  risk:     { color: "#E24B4A", bg: "#391010", label: "Risque" },
  mandate:  { color: "#52B0E0", bg: "#0e2d3d", label: "Mandat" },
  chain:    { color: "#EF9F27", bg: "#3a2810", label: "Chaîne" },
  opp:      { color: "#1D9E75", bg: "#0a2d22", label: "Opportunité" },
  signal:   { color: "#7F77DD", bg: "#1d1a3a", label: "Signal" },
  re_asset: { color: "#4A89DC", bg: "#0d1f3a", label: "Immo" },
  brvm:     { color: "#378ADD", bg: "#0d1f3a", label: "Marchés fin." },
  capital:  { color: "#52B0E0", bg: "#0e2d3d", label: "Capital" },
};

export const ROUTE_BY_TYPE = {
  risk: "/app/risques", mandate: "/app/courtage", chain: "/app/chainage",
  opp: "/app/origination", signal: "/app/feed", re_asset: "/app/immobilier",
  brvm: "/app/brvm", capital: "/app/sources-capital",
};

function GraphNode({ data }) {
  const s = TYPE_STYLE[data.type] || TYPE_STYLE.signal;
  const isEntity = data.type === "entity";
  return (
    <div
      style={{
        background: s.bg, border: `1.5px solid ${s.color}`,
        borderRadius: isEntity ? 999 : 8,
        padding: isEntity ? "10px 16px" : "6px 10px",
        maxWidth: 190, boxShadow: data.expanded ? `0 0 18px ${s.color}55` : "none",
        cursor: "pointer",
      }}
      title={isEntity ? "Double-clic pour déployer les connexions" : "Clic pour ouvrir le module"}
    >
      <div style={{ fontSize: 8, textTransform: "uppercase", letterSpacing: 1, color: s.color, marginBottom: 2 }}>
        {s.label}{data.subtype && data.subtype !== "—" ? ` · ${data.subtype}` : ""}
      </div>
      <div style={{ fontSize: isEntity ? 12 : 10, fontWeight: isEntity ? 700 : 500, color: "#f4f4f5", lineHeight: 1.25 }}>
        {data.label}
      </div>
    </div>
  );
}
const nodeTypes = { noam: GraphNode };

export function layout(nodes, edges) {
  const entities = nodes.filter((n) => n.type === "entity");
  const sats = nodes.filter((n) => n.type !== "entity");
  const pos = {};
  const R = Math.max(320, entities.length * 42);
  entities.forEach((n, i) => {
    const a = (i / Math.max(1, entities.length)) * 2 * Math.PI;
    pos[n.id] = { x: Math.cos(a) * R, y: Math.sin(a) * R };
  });
  // satellites : autour de la moyenne de leurs entités connectées, poussés vers l'extérieur
  const linksOf = {};
  edges.forEach((e) => {
    (linksOf[e.target] = linksOf[e.target] || []).push(e.source);
    (linksOf[e.source] = linksOf[e.source] || []).push(e.target);
  });
  sats.forEach((n, i) => {
    const anchors = (linksOf[n.id] || []).map((id) => pos[id]).filter(Boolean);
    if (anchors.length) {
      const ax = anchors.reduce((s, p) => s + p.x, 0) / anchors.length;
      const ay = anchors.reduce((s, p) => s + p.y, 0) / anchors.length;
      const norm = Math.sqrt(ax * ax + ay * ay) || 1;
      const push = 190 + (i % 4) * 55;
      const jitter = ((i * 137) % 90) - 45;
      pos[n.id] = { x: ax + (ax / norm) * push + jitter, y: ay + (ay / norm) * push + ((i * 71) % 80) - 40 };
    } else {
      const a = (i / Math.max(1, sats.length)) * 2 * Math.PI;
      pos[n.id] = { x: Math.cos(a) * (R + 380), y: Math.sin(a) * (R + 380) };
    }
  });
  return pos;
}

export default function GraphExplorer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rawNodes, setRawNodes] = useState([]);
  const [rawEdges, setRawEdges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const expandedRef = useRef(new Set());
  const flowRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/graph/overview", { params: { limit: 60 } });
      setRawNodes(r.data?.nodes || []);
      setRawEdges(r.data?.edges || []);
      setStats(r.data?.stats || null);
      expandedRef.current = new Set();
    } catch (e) { notify.error(fmtError(e, "Chargement du graphe impossible")); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const expandEntity = useCallback(async (nodeId) => {
    const entityId = nodeId.replace("entity:", "");
    if (expandedRef.current.has(nodeId)) return;
    expandedRef.current.add(nodeId);
    try {
      const r = await api.get(`/workspace/annuaire/entities/${entityId}/graph`, { params: { limit_per_type: 6 } });
      const newNodes = (r.data?.nodes || []).filter((n) => n.id !== nodeId);
      const newEdges = r.data?.edges || [];
      let added = 0;
      setRawNodes((prev) => {
        const ids = new Set(prev.map((n) => n.id));
        const fresh = newNodes.filter((n) => !ids.has(n.id));
        added = fresh.length;
        return [...prev, ...fresh];
      });
      setRawEdges((prev) => {
        const keys = new Set(prev.map((e) => `${e.source}>${e.target}`));
        return [...prev, ...newEdges.filter((e) => !keys.has(`${e.source}>${e.target}`))];
      });
      setTimeout(() => {
        if (added > 0) notify.success(`${added} connexion(s) déployée(s).`);
        else notify.info ? notify.info("Toutes les connexions de cette entité sont déjà affichées.") : notify.success("Toutes les connexions sont déjà affichées.");
      }, 50);
    } catch (e) { notify.error(fmtError(e)); }
  }, []);

  const { flowNodes, flowEdges } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pos = layout(rawNodes, rawEdges);
    const flowNodes = rawNodes.map((n) => ({
      id: n.id,
      type: "noam",
      position: pos[n.id] || { x: 0, y: 0 },
      data: { ...n, expanded: expandedRef.current.has(n.id) },
      style: q && !(n.label || "").toLowerCase().includes(q) ? { opacity: 0.18 } : undefined,
    }));
    const flowEdges = rawEdges.map((e, i) => ({
      id: `e${i}`,
      source: e.source, target: e.target,
      style: { stroke: "#3f3f46", strokeWidth: Math.max(1, (e.weight || 0.5) * 2.4), opacity: 0.7 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#3f3f46", width: 12, height: 12 },
    }));
    return { flowNodes, flowEdges };
  }, [rawNodes, rawEdges, search]);

  const onNodeDoubleClick = useCallback((_, node) => {
    if (node.data?.type === "entity") expandEntity(node.id);
  }, [expandEntity]);

  const onNodeClick = useCallback((_, node) => {
    const type = node.data?.type;
    if (type === "entity") return;
    const route = ROUTE_BY_TYPE[type];
    if (route) navigate(route);
  }, [navigate]);

  return (
    <div className="space-y-4 flex flex-col h-[calc(100vh-120px)]" data-testid="graph-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="noam-overline">{t("graph.overline", { defaultValue: "Réseau de l'organisation" })}</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Network className="w-5 h-5 text-[#EFD127]" /> Noam Graph
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            {t("graph.subtitle", { defaultValue: "Entités, risques, mandats, chaînes et opportunités en un seul réseau navigable. Double-cliquez sur une entité pour déployer ses connexions." })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-md px-2 py-1.5">
            <Search className="w-3.5 h-3.5 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Surligner un nœud…"
              className="bg-transparent border-0 outline-none text-xs w-40 text-zinc-200"
              data-testid="graph-search" />
          </div>
          <button onClick={load} className="btn-secondary text-xs" data-testid="graph-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser
          </button>
        </div>
      </div>

      {stats && (
        <div className="flex items-center gap-2 flex-wrap text-[10px]" data-testid="graph-stats">
          {Object.entries(TYPE_STYLE).map(([k, s]) => {
            const count = k === "entity" ? stats.entities
              : k === "risk" ? stats.risks : k === "mandate" ? stats.mandates
            : k === "chain" ? stats.chains : k === "opp" ? stats.opps
              : k === "signal" ? stats.signals : null;
            if (count == null) return null;
            return (
              <span key={k} className="px-2 py-1 rounded-full border" style={{ borderColor: `${s.color}55`, color: s.color, background: `${s.color}0f` }}>
                {s.label} · {count}
              </span>
            );
          })}
          <span className="text-zinc-500 ml-1">{stats.total_entities_in_annuaire} entités dans l'Annuaire</span>
        </div>
      )}

      <div className="noam-card p-0 flex-1 min-h-[480px] overflow-hidden" data-testid="graph-canvas">
        {loading ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Construction du graphe…
          </div>
        ) : rawNodes.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-zinc-500 text-sm gap-2">
            <Maximize2 className="w-8 h-8 opacity-40" />
            Aucune donnée à cartographier. Ajoutez des entités dans l'Annuaire.
          </div>
        ) : (
          <ReactFlow
            ref={flowRef}
            nodes={flowNodes}
            edges={flowEdges}
            nodeTypes={nodeTypes}
            onNodeDoubleClick={onNodeDoubleClick}
            onNodeClick={onNodeClick}
            fitView
            minZoom={0.08}
            proOptions={{ hideAttribution: true }}
            style={{ background: "transparent" }}
          >
            <Background color="#27272a" gap={28} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
